import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, type ReactNode } from 'react';

import * as authApi from '@/api/auth';
import { ApiError } from '@/api/client';
import { AuthContext, SESSION_QUERY_KEY, type AuthContextValue } from '@/features/auth/context';

/**
 * Session state for the whole app.
 *
 * There is no token to hold: authentication lives in an httpOnly cookie the
 * browser sends automatically. This provider therefore holds no credential at
 * all — only the *result* of asking the server who we are.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const {
    data: session,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: authApi.fetchSession,
    // A 401 is the answer ("not signed in"), not a transient failure.
    retry: false,
    staleTime: 5 * 60_000,
    // Never surface the 401 as an error state; absence of a session IS the
    // information the router needs.
    throwOnError: false,
  });

  /**
   * Discard every cached query EXCEPT the session.
   *
   * Called on login, logout and organisation switch. Dropping the tenant's
   * cached data is the important part: without it, customers and quotations
   * fetched for organisation A stay on screen under organisation B — a
   * cross-tenant leak invisible in any single-tenant demo.
   *
   * The session query is spared deliberately. queryClient.clear() removes it
   * from the cache while this hook's observer still holds a reference to it,
   * so the refetch below lands on an orphaned query and the resolved session
   * never reaches the provider — the switch silently appears to do nothing.
   */
  const resetCache = useCallback(async () => {
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0],
    });

    await refetch();
  }, [queryClient, refetch]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user: session?.user ?? null,
      memberships: session?.memberships ?? [],
      activeOrganization: session?.active_organization ?? null,
      activeRole: session?.active_role ?? null,
      permissions: session?.permissions ?? [],
      isLoading,
      isAuthenticated: session?.user != null,

      login: async (payload) => {
        await authApi.login(payload);
        await resetCache();
      },

      register: async (payload) => {
        await authApi.register(payload);
        await resetCache();
      },

      logout: async () => {
        try {
          await authApi.logout();
        } catch (error) {
          // A already-expired session still means "signed out" locally; only
          // re-raise something the user could act on.
          if (!(error instanceof ApiError) || error.status !== 401) throw error;
        }
        // Full clear IS right here: there is no session left to preserve.
        queryClient.clear();
        await refetch();
      },

      switchOrganization: async (organizationId) => {
        await authApi.switchOrganization(organizationId);
        await resetCache();
      },
    }),
    [session, isLoading, resetCache, queryClient, refetch],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}
